import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { fetchPosts } from "../services/api";
import PostCard from "./PostCard";

/**
 * PostList fetches published blog posts page-by-page and renders a card for
 * each one. A "Load more" button appears when there are additional pages.
 *
 * States handled:
 *  - loading     → centered CircularProgress spinner (initial load only)
 *  - error       → descriptive error message
 *  - empty       → "No posts yet." notice
 *  - data        → list of PostCard components + optional Load more button
 *  - loadingMore → Load more button is disabled and shows inline spinner
 */
const PostList = () => {
  const theme = useTheme();

  const [posts, setPosts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // Initial load: fetch page 1 once on mount.
  useEffect(() => {
    let cancelled = false;

    const loadInitial = async () => {
      try {
        const data = await fetchPosts(1);
        if (!cancelled) {
          setPosts(data.posts);
          setMeta(data.meta);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message ?? "Failed to load posts. Please try again.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadInitial();

    // Cleanup: ignore in-flight response if the component unmounts before the
    // fetch completes, preventing a setState call on an unmounted component.
    return () => {
      cancelled = true;
    };
  }, []);

  // Append the next page of posts to the existing list.
  const handleLoadMore = useCallback(async () => {
    if (!meta || meta.current_page >= meta.total_pages) return;

    const nextPage = meta.current_page + 1;
    setLoadingMore(true);

    try {
      const data = await fetchPosts(nextPage);
      setPosts((prev) => [...prev, ...data.posts]);
      setMeta(data.meta);
    } catch (err) {
      // Surface the error without wiping out the posts already shown.
      setError(err.message ?? "Failed to load more posts. Please try again.");
    } finally {
      setLoadingMore(false);
    }
  }, [meta]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress
          size={36}
          sx={{ color: theme.palette.primary.main }}
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 3,
          borderLeft: `3px solid ${theme.palette.string.main}`,
          backgroundColor: "background.paper",
          borderRadius: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.string.main,
            fontFamily: "'Fira Code', monospace",
          }}
        >
          {`// Error: ${error}`}
        </Typography>
      </Box>
    );
  }

  if (posts.length === 0) {
    return (
      <Typography
        variant="body1"
        sx={{
          color: theme.palette.comment.main,
          fontFamily: "'Fira Code', monospace",
        }}
      >
        {`// No posts yet.`}
      </Typography>
    );
  }

  const hasMore = meta && meta.current_page < meta.total_pages;

  return (
    <Box>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {posts.map((post) => (
          <Box component="li" key={post.slug ?? post.id}>
            <PostCard post={post} />
          </Box>
        ))}
      </Box>

      {hasMore && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button
            onClick={handleLoadMore}
            disabled={loadingMore}
            variant="outlined"
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              fontFamily: "'Fira Code', monospace",
              textTransform: "none",
              // Preserve button width while the label swaps to avoid layout shift.
              minWidth: 140,
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: `${theme.palette.primary.main}1a`,
              },
              "&.Mui-disabled": {
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                opacity: 0.6,
              },
            }}
            startIcon={
              loadingMore ? (
                <CircularProgress
                  size={14}
                  sx={{ color: theme.palette.primary.main }}
                />
              ) : null
            }
          >
            {loadingMore ? "Loading..." : "Load more"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PostList;
